import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { FormLabel } from '@components/form-label';
import { HideContainer } from '@components/hide-container';
import { IconEndpoint } from '@components/icons/endpoint';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useGetTravellerJourneySource } from 'api/traveller-journeys/hooks';

type ApiSidebarProps = {
  travellerJourneyId: string;
};

export const useApiSidebar = () => {
  return useSidebar(ApiSidebar, {
    variant: 'REGULAR',
  });
};

const ApiSidebar = ({ travellerJourneyId }: ApiSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading, isError } = useGetTravellerJourneySource(travellerJourneyId);

  let overrideContent;
  if (isLoading) {
    overrideContent = (
      <Sidebar.Content>
        <Sidebar.Spinner />
      </Sidebar.Content>
    );
  }
  if (isError || !data) {
    overrideContent = (
      <Sidebar.Content>
        <Text font="regular14" color="textSecondary" text={t('noData')} />
      </Sidebar.Content>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconEndpoint} iconColor="elementBlue100" title={t('title')} />
      {overrideContent ?? (
        <Sidebar.Content>
          <Stack space={6}>
            <HideContainer title={t('triggerDetails')}>
              <Stack space={4}>
                <div>
                  <FormLabel text={t('created')} />
                  <Text
                    font="regular14"
                    text={abbreviateDate(data?.dateCreated ?? '', 'en', 'datetime')}
                  />
                </div>
                <div>
                  <FormLabel text={t('source')} />
                  <Text font="regular14" text={t('apiRequest')} />
                </div>
                {data?.sourceInfo.name ? (
                  <div>
                    <FormLabel text={t('createdBy')} />
                    <Text font="regular14" text={data.sourceInfo.name} />
                    {data.sourceInfo.email ? (
                      <Text font="regular12" color="textSecondary" text={data.sourceInfo.email} />
                    ) : null}
                  </div>
                ) : null}
                <div>
                  <FormLabel text={t('autoStart')} />
                  <Text font="regular14" text={data?.isAutoStart ? t('enabled') : t('disabled')} />
                </div>
              </Stack>
            </HideContainer>
          </Stack>
        </Sidebar.Content>
      )}
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button text={t('close')} variant="secondary" onClick={hideSidebar} />
        </ButtonGroup>
      </Sidebar.Footer>
    </Sidebar>
  );
};

const locale = {
  en: {
    title: 'API trigger',
    triggerDetails: 'Trigger details',
    created: 'Created',
    source: 'Source',
    apiRequest: 'API request',
    createdBy: 'Created by',
    autoStart: 'Auto-start journey',
    enabled: 'Enabled',
    disabled: 'Disabled',
    noData: 'No data available for this trigger.',
    close: 'Close',
  },
};
